/**
 * <s-gallery-carousel> — one-slide-at-a-time product media carousel.
 *
 * Required markup:
 *   [data-carousel-track]        — horizontally scrolling, snap-aligned slide track.
 *   [data-carousel-track] > *    — slides, each the full track width.
 *   button[command="--prev"|"--next"] with commandfor = this element's id.
 *   button[command="--go"][data-index] — optional thumbnails.
 *   [data-carousel-status]       — optional "1 / 9" counter; data-label-template uses [current] and [total].
 */
class SGalleryCarousel extends HTMLElement {
  #ac;
  #track;
  #index = 0;
  #raf = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.addEventListener('command', this.#onCommand, opts);
    this.#track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });
    this.#track.addEventListener('keydown', this.#onKeydown, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  get #slides() {
    return Array.from(this.#track.children);
  }

  #onCommand = (event) => {
    if (event.command === '--prev') this.#goTo(this.#index - 1);
    else if (event.command === '--next') this.#goTo(this.#index + 1);
    else if (event.command === '--go') this.#goTo(Number(event.source?.dataset.index ?? 0));
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (event.key === 'ArrowRight') !== rtl;
      event.preventDefault();
      this.#goTo(this.#index + (forward ? 1 : -1));
    }
  };

  #goTo(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    const clamped = Math.max(0, Math.min(slides.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slides[clamped].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    this.#index = clamped;
    this.#update();
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (index !== this.#index) {
        this.#index = index;
        this.#update();
      }
    });
  };

  #update() {
    const total = this.#slides.length;
    const prev = this.querySelector('[command="--prev"]');
    const next = this.querySelector('[command="--next"]');
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= total - 1;

    this.querySelectorAll('[command="--go"]').forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.#index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    const status = this.querySelector('[data-carousel-status]');
    if (status) {
      const template = status.dataset.labelTemplate || '[current] / [total]';
      status.textContent = template.replace('[current]', String(this.#index + 1)).replace('[total]', String(total));
    }
  }
}

if (!customElements.get('s-gallery-carousel')) {
  customElements.define('s-gallery-carousel', SGalleryCarousel);
}
